/**
 * Analytics MV2 query API — isolated from MV1 pages/Analytics.jsx.
 * Per-endpoint fetchers (MV1-style resilience).
 */
import api, { getAnalytics } from "@/lib/api";

export const ANALYTICS_QUERY_KEY = "analytics";
export const ANALYTICS_CORE_QUERY_KEY = "analytics-core";
export const ANALYTICS_EXPENSE_SUMMARY_QUERY_KEY = "analytics-expense-summary";

function isAbortError(error, signal) {
  if (signal?.aborted) return true;
  return error?.name === "AbortError" || error?.code === "ERR_CANCELED";
}

async function safeSectionFetch(label, signal, fetcher, fallback) {
  try {
    const data = await fetcher();
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    return { data, error: null };
  } catch (error) {
    if (isAbortError(error, signal)) throw error;
    console.error(`Analytics MV2: failed to fetch ${label}:`, error);
    return { data: fallback, error };
  }
}

export async function fetchAnalyticsCoreFromApi({ start, end }, signal) {
  const { data } = await safeSectionFetch(
    "analytics",
    signal,
    () => getAnalytics({ start_date: start, end_date: end }).then((r) => r.data),
    null
  );
  return data;
}

export async function fetchAnalyticsExpenseSummaryFromApi({ start, end }, signal) {
  const { data } = await safeSectionFetch(
    "expense-summary",
    signal,
    () =>
      api
        .get("/expense-summary", { params: { start_date: start, end_date: end }, signal })
        .then((r) => r.data),
    null
  );
  return data;
}

/** Composed fetch — safe section fetchers; kept for guard wiring. */
export async function fetchAnalyticsFromApi({ start, end }, signal) {
  const [analytics, expenseSummary] = await Promise.all([
    fetchAnalyticsCoreFromApi({ start, end }, signal),
    fetchAnalyticsExpenseSummaryFromApi({ start, end }, signal),
  ]);
  return { analytics, expenseSummary };
}
